import { Button } from '@mui/material';

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PhoneWithControlAndPrefixInput, SelectWithControl, TextInputWithControl } from 'src/components';
import { getCitiesListSelector } from 'src/features/appSettings';
import { AgencyPatchPayload } from 'src/features/dashboard/hooks';
import { currencySymbolMapping } from 'src/helpers';
import { useEditAgencyForm } from '../hooks';
import { EditAgencyInputs } from './EditAgencyInputs';

type Props = {
  initialValues: EditAgencyInputs;
  onTouched: (isDirty: boolean) => void;
  errorEdit?: Error | null;
  submitEdit: (data: AgencyPatchPayload) => void;
  isParent: boolean;
};

export const EditAgencyForm = ({ initialValues, onTouched, errorEdit, submitEdit, isParent }: Props) => {
  const { control, handleSubmit, errors, isDirty } = useEditAgencyForm({ initialValues, isParent });

  const cities = useSelector(getCitiesListSelector);

  useEffect(() => {
    onTouched(isDirty);
  }, [isDirty, onTouched]);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    (data: EditAgencyInputs) => {
      submitEdit({
        ...data,
      } as AgencyPatchPayload);
    },
    [submitEdit],
  );

  return (
    <div className="bg-white px-2 py-4 ml-5 w-4/5">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <TextInputWithControl
            control={control}
            name="name"
            placeholder={t('dashboard-agencies-edit.form-agency-name')}
            error={errors.name}
            showUpLabel
          />
        </div>
        <div>
          <PhoneWithControlAndPrefixInput
            control={control}
            name="tel"
            placeholder={t('dashboard-agencies-edit.form-agency-tel')}
            error={errors?.tel}
            showUpLabel
            copyNumbersOnly={false}
          />
        </div>

        <div>
          <TextInputWithControl
            control={control}
            name="email"
            placeholder={t('dashboard-agencies-edit.form-agency-email')}
            error={errors.email}
            showUpLabel
            type="email"
          />
        </div>
        <div>
          <TextInputWithControl
            control={control}
            name="address"
            placeholder={t('dashboard-agencies-edit.form-agency-address')}
            error={errors.address}
            showUpLabel
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <TextInputWithControl
              control={control}
              name="lat"
              placeholder={t('dashboard-agencies-edit.form-agency-lat')}
              error={errors.lang}
              type="number"
              showUpLabel
            />
          </div>

          <div>
            <TextInputWithControl
              control={control}
              name="lang"
              placeholder={t('dashboard-agencies-edit.form-agency-lang')}
              error={errors.lang}
              showUpLabel
              type="number"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <TextInputWithControl
              control={control}
              name="pricePerKilo"
              placeholder={t('dashboard-agencies-edit.form-agency-price-per-kilo', {
                currency: currencySymbolMapping(initialValues.currency as string),
              })}
              error={errors.pricePerKilo}
              showUpLabel
              type="number"
            />
          </div>
          <div>
            <TextInputWithControl
              control={control}
              name="priceMini"
              placeholder={t('dashboard-agencies-edit.form-agency-price-mini', {
                currency: currencySymbolMapping(initialValues.currency as string),
              })}
              error={errors.priceMini}
              showUpLabel
              type="number"
            />
          </div>
        </div>

        <div>
          <SelectWithControl
            control={control}
            name="city"
            data={cities ?? []}
            placeholder={t('dashboard-agencies-edit.form-agency-city')}
            error={errors.city}
            showUpLabel
          />
        </div>

        <div className="mt-4">
          {!!errorEdit && <div className="text-red-600 text-center">{t(errorEdit.message)}</div>}
          <Button variant="contained" color="primary" type="submit">
            {t('dashboard-agencies-edit.form-agency-submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};
